import { Box, HStack, Icon, Stack, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { LuChevronRight } from 'react-icons/lu';
import type { ReactNode } from 'react';

type Tone = 'default' | 'positive' | 'warning' | 'danger';

const TONE_COLOR: Record<Tone, string> = {
  default: 'fg',
  positive: 'green.700',
  warning: 'amber.700',
  danger: 'red.600',
};

type Props = {
  title: string;
  value: ReactNode;
  caption?: string;
  tone?: Tone;
  linkTo?: string;
  delta?: { pct: number | null; label?: string };
  /** Demoted variant for secondary strips: smaller value that never breaks mid-number. */
  compact?: boolean;
};

export function KpiCard({ title, value, caption, tone = 'default', linkTo, delta, compact = false }: Props) {
  const inner = (
    <Stack
      gap="2"
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border"
      rounded="lg"
      p={{ base: '4', md: '5' }}
      h="full"
      _hover={linkTo ? { borderColor: 'brand.300', bg: 'bg.subtle' } : undefined}
      transition="all 120ms ease"
    >
      <HStack justify="space-between" align="flex-start" gap="2">
        <Text fontSize="xs" color="fg.muted" textTransform="uppercase" letterSpacing="0.06em" fontWeight="700">
          {title}
        </Text>
        {linkTo && <Icon as={LuChevronRight} color="fg.muted" />}
      </HStack>
      <Text
        fontSize={compact ? { base: 'lg', md: 'xl' } : { base: 'xl', md: '3xl' }}
        fontWeight="700"
        color={TONE_COLOR[tone]}
        lineHeight="1.1"
        whiteSpace="nowrap"
      >
        {value}
      </Text>
      {delta && (() => {
        const up = delta.pct !== null && delta.pct > 0.5;
        const down = delta.pct !== null && delta.pct < -0.5;
        const color = up ? 'green.700' : down ? 'red.600' : 'fg.muted';
        const arrow = up ? '▲' : down ? '▼' : '—';
        const text = delta.pct === null ? 's/d' : `${Math.abs(delta.pct).toFixed(0)}%`;
        return (
          <HStack gap="1.5">
            <Text fontSize="xs" fontWeight="700" color={color}>
              {arrow} {text}
            </Text>
            {delta.label && (
              <Text fontSize="2xs" color="fg.muted">
                {delta.label}
              </Text>
            )}
          </HStack>
        );
      })()}
      {caption && (
        <Text fontSize="xs" color="fg.muted">
          {caption}
        </Text>
      )}
    </Stack>
  );

  if (linkTo) {
    return (
      <Box as={RouterLink} {...({ to: linkTo } as { to: string })} display="block" textDecoration="none">
        {inner}
      </Box>
    );
  }
  return inner;
}
